import {
	getCoreRowModel,
	getExpandedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table'
import update from 'immutability-helper'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAppContext } from '@/hooks'

import { Empty } from '../Layout'
import { TableWrap } from './styles'
import { TableHeader } from './TableHeader'
import { TableRow } from './TableRow'
import { TableRowNoDnd } from './TableRowNoDnd'
import { TableProps } from './types'
import { createSortPlacementMemory, placeRowsForActiveSort } from './utils'

export const Table = ({
	actionCellWidth = '3.75rem',
	alwaysShowRowActions = false,
	columns = [],
	data,
	isEditMode,
	hasDnd = true,
	isRowEditDisabled,
	hasLastRowEdit,
	hasLeftAction = true,
	hasRightAction = true,
	handleChange,
	onEdit,
	onDelete,
	onSaveData,
	onRowSelect,
	onSortingChange,
	sorting: controlledSorting,
	maxWidth,
	scrollWithParent = false,
	columnResizeMode = 'onChange',
}: TableProps) => {
	const [items, setItems] = useState(data)
	const [columnSizing, setColumnSizing] = useState({})
	const [sorting, setSorting] = useState<SortingState>([])

	const { t } = useAppContext()

	useEffect(() => {
		if (!isEqual(data, items)) {
			setItems(data)
		}
	}, [data])

	const visibleColumns = useMemo(
		() => columns.filter((column) => column.isVisible),
		[columns],
	)

	const sortingState = controlledSorting ?? sorting
	const handleSortingChange = onSortingChange ?? setSorting
	const sortPlacementRef = useRef(createSortPlacementMemory())

	const table = useReactTable({
		data: items,
		columns: visibleColumns,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getSubRows: (row) => row?.subRows,
		columnResizeMode,
		columnResizeDirection: 'ltr',
		enableColumnResizing: true,
		autoResetSorting: false,
		state: {
			columnSizing,
			sorting: sortingState,
		},
		onColumnSizingChange: setColumnSizing,
		onSortingChange: handleSortingChange,
		debugTable: false,
		meta: {
			handleChange,
		},
	})

	const onMoveRow = useCallback(
		(dragIndex: number, dropIndex: number) => {
			const draggedItem = items?.[dragIndex]

			const replacedItems = update(items, {
				$splice: [
					[dragIndex, 1],
					[dropIndex, 0, draggedItem],
				],
			})

			const updatedItems = replacedItems?.map((item, index) => {
				return {
					...item,
					order: index,
				}
			})

			setItems(updatedItems)
			onSaveData && onSaveData(updatedItems)
		},
		[items, onSaveData],
	)

	const tableRows = placeRowsForActiveSort(
		table.getRowModel().rows,
		JSON.stringify(sortingState),
		sortPlacementRef.current,
	)

	return (
		<TableWrap
			$isSubRowTable
			$maxWidth={maxWidth}
			$actionCellWidth={actionCellWidth}
			$scrollWithParent={scrollWithParent}
		>
			<table>
				{hasLeftAction && (
					<colgroup>
						<col className="w-10" />
					</colgroup>
				)}
				<TableHeader
					hasRightAction={hasRightAction}
					hasLeftAction={hasLeftAction}
					table={table}
				/>
				<tbody>
					{tableRows.map((row, index) => {
						const isLastRow = index === tableRows.length - 1

						const commonProps = {
							actionCellWidth,
							alwaysShowRowActions,
							isEditMode,
							isLastRow,
							hasLastRowEdit,
							hasLeftAction,
							hasRightAction,
							isRowEditDisabled,
							onDelete,
							onEdit,
							onMoveRow,
							onSelect: onRowSelect,
							row,
						}

						return hasDnd ? (
							<TableRow key={row.id} {...commonProps} />
						) : (
							<TableRowNoDnd key={row.id} {...commonProps} />
						)
					})}
				</tbody>
			</table>
			{!tableRows.length && <Empty>{t('NOTHING_HERE')}</Empty>}
		</TableWrap>
	)
}
